import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type SistemaTrava = 'contrato' | 'tramitacao' | 'portabilidade' | 'qualidade';

export interface JustificativaTrava {
  id: string;
  origem: 'fila' | 'qualidade';
  texto: string;
  motivo: string | null;
  em: string;
  recusada: boolean;
}

export interface PedidoDaTrava {
  pedido_id: string;
  numero_pedido: number | null;
  cliente: string | null;
  batido: boolean;
  justificativa: JustificativaTrava | null;
}

export interface PessoaPlacar {
  id: string;
  nome: string;
  funcao: string;
  sistema: SistemaTrava;
  linha: string | null;
  primeira_acao: string | null;
  ultima_acao: string | null;
  /** Minutos desde a última ação, sem o almoço (fn_trava_horas). */
  sem_acao_min?: number | null;
  em_almoco?: boolean;
  justificativas_hoje: number;
  justificativas_repetidas: number;
  travas_hoje: number;
  cobrancas_sem_ler: number;
  trava: { sistema: SistemaTrava; fila: string; travado_em: string; minutos?: number; pedidos: PedidoDaTrava[] } | null;
  /** Só da BKO Tramitação: o ritmo do dia (fn_bko_tramitacao_ritmo_dia). */
  ritmo?: RitmoDia | null;
  dia: { em: string; tipo: string; texto: string; fila?: string; pedidos?: number; estourou?: boolean }[];
}

export interface RitmoDia {
  erro_solar_agora: number;
  erro_solar_pendentes: number;
  erro_solar_mais_antigo_horas: number | null;
  entraram_hoje: number;
  sairam_hoje: number;
  blocos: number;
  blocos_no_tempo: number;
  blocos_estourados: number;
  estourados_seguidos: boolean | null;
  consulta_media_seg: number | null;
  abertura: { travado_em: string; batida_em: string | null; pedidos: number } | null;
  segmentos: { fila: string; de: string; ate: string | null; estourou: boolean }[];
  pendencias: { pedido_id: string; numero_pedido: number | null; cliente: string | null; motivo: string; texto: string; em: string }[];
}

export interface Placar { agora: string; pessoas: PessoaPlacar[]; minha_linha: string | null }

export interface PedidoContrato { item_id: string; pedido_id: string; numero_pedido: number | null; cliente: string | null; coluna: string }

export interface PedidoQualidade { pedido_id: string; numero_pedido: number | null; cliente: string | null; coluna: string }

export interface RecadoSupervisora { id: string; texto: string; criado_em: string; autor: string }

async function chamar<T>(path: string, metodo: 'GET' | 'POST' = 'GET', body?: unknown): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  const r = await fetch(`${base}/v1/backoffice/${path}`, {
    method: metodo,
    headers: { Authorization: `Bearer ${session.access_token}`, ...(metodo === 'POST' ? { 'Content-Type': 'application/json' } : {}) },
    body: metodo === 'POST' ? JSON.stringify(body ?? {}) : undefined,
  });
  const corpo = await r.json().catch(() => ({ error: 'O CRM não respondeu corretamente.' }));
  if (!r.ok) throw new Error(corpo.error || 'Não foi possível concluir.');
  return corpo.data as T;
}

export function useBackofficePlacar(ativo: boolean) {
  return useQuery({
    queryKey: ['backoffice-placar'],
    enabled: ativo,
    refetchInterval: 60_000,
    staleTime: 20_000,
    queryFn: () => chamar<Placar>('placar'),
  });
}

export function usePedidosContrato(usuarioId: string | null) {
  return useQuery({
    queryKey: ['backoffice-placar-pedidos-contrato', usuarioId],
    enabled: !!usuarioId,
    queryFn: () => chamar<PedidoContrato[]>(`placar/pedidos-contrato/${usuarioId}`),
  });
}

export function usePedidosQualidade(usuarioId: string | null) {
  return useQuery({
    queryKey: ['backoffice-placar-pedidos-qualidade', usuarioId],
    enabled: !!usuarioId,
    queryFn: () => chamar<PedidoQualidade[]>(`placar/pedidos-qualidade/${usuarioId}`),
  });
}

export function useAcoesPlacar() {
  const qc = useQueryClient();
  const recarregar = () => qc.invalidateQueries({ queryKey: ['backoffice-placar'] });
  return {
    cobrar: useMutation({
      mutationFn: (b: { usuarioId: string; texto: string }) =>
        chamar<{ whatsapp: 'enviada' | 'sem_linha' | 'falhou'; linha_de: string | null; linha_para: string | null; erro: string | null }>('placar/cobrar', 'POST', b),
      onSuccess: recarregar,
    }),
    recusar: useMutation({
      mutationFn: (b: { justificativaId: string; origem: 'fila' | 'qualidade'; motivo: string; texto?: string }) =>
        chamar<{ recusada: boolean }>('placar/recusar', 'POST', b),
      onSuccess: recarregar,
    }),
    anotar: useMutation({
      mutationFn: (b: { usuarioId: string; texto: string }) => chamar<{ anotado: boolean }>('placar/anotar', 'POST', b),
      onSuccess: recarregar,
    }),
    passar: useMutation({
      mutationFn: async (b: { itemIds: string[]; paraId: string; motivo: string }) => {
        const rpc = supabase.rpc.bind(supabase) as unknown as (fn: string, a: Record<string, unknown>) => Promise<{ error: { message?: string } | null }>;
        for (const itemId of b.itemIds) {
          const { error } = await rpc('fn_planilha_ajustar_bko', { p_item_id: itemId, p_bko_id: b.paraId, p_motivo: b.motivo });
          if (error) throw new Error(error.message || 'Não foi possível passar o pedido.');
        }
      },
      onSuccess: () => {
        recarregar();
        qc.invalidateQueries({ queryKey: ['backoffice-placar-pedidos-contrato'] });
      },
    }),
    passarAnalista: useMutation({
      mutationFn: (b: { pedidoIds: string[]; paraId: string; motivo: string }) =>
        chamar<{ passados: number }>('placar/passar-analista', 'POST', b),
      onSuccess: () => {
        recarregar();
        qc.invalidateQueries({ queryKey: ['backoffice-placar-pedidos-qualidade'] });
      },
    }),
  };
}

export function useRecadosSupervisora(ativo: boolean) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['backoffice-recados'],
    enabled: ativo,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: () => chamar<RecadoSupervisora[]>('recados'),
  });
  const lido = useMutation({
    mutationFn: (id: string) => chamar<{ lido: boolean }>(`recados/${id}/lido`, 'POST', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backoffice-recados'] }),
  });
  return { recados: q.data ?? [], marcarLido: lido };
}
